import { analytics } from 'common/utils/analytics';
import untypedStore from 'client/store';
import { sendVideoEvent } from 'client/actions/video-events';
import type { ClientReducerState, ClientThunkDispatch } from 'client/reducers';
import { selectStoryId } from 'src/routes/client/reducers/story/selectors';
import { selectCardId } from 'src/routes/client/reducers/card/selectors';
import type { IMedia as OriginalIMedia } from './types';

const store = untypedStore as {
	getState: () => ClientReducerState;
	dispatch: ClientThunkDispatch;
};

export type IMedia = OriginalIMedia & {
	id: string;
	storyId: string;
	cardId: string;
	layerName: string;
	playbackId: string | null;
	transcodingId?: string | null;
	videoContentId?: string;
};

type AnalyticsEvent =
	| {
			type: 'impression';
			media: IMedia;
			details?: null;
	  }
	| {
			type: 'play';
			media: IMedia;
			details?: null;
	  }
	| {
			type: 'ended';
			media: IMedia;
			details?: null;
	  }
	| {
			type: 'watchtime';
			media: IMedia;
			details: {
				watchtimeJumped: boolean;
				watchtimeLocation: number;
			};
	  }
	| { type: 'engaged_3s'; media: IMedia; details?: null };

interface IAnalyticsObserver {
	track(event: AnalyticsEvent): Promise<unknown>;
}

interface IAnalytics {
	registerMedia: (media: IMedia) => void;
	unregisterMedia: (id: string) => void;
}

type ProcessedEventData = {
	timestamp: number;
};

/**
 * impression: when player initially enters view of user
 *
 * play: when play is started (autoplay or manual, only once)
 *
 * watchtime: send periodically while user is watching the video,
 * and if the user jumps in the timeline of the video send 2 events,
 * one for the time before the jump with watchtimeJumped false,
 * and then another one with new location with watchtimeJumped true.
 */
class Analytics implements IAnalytics {
	static DEFAULT_PROCESSORS_AMOUNT = 5;
	static WATCHTIME_INTERVAL = 10_000;

	private _medias: {
		[mediaId: string]: {
			media: IMedia;
			data?: { currentTime?: number; wasSeeked?: boolean; engagedFired?: boolean };
			cleanup: () => void;
		};
	} = {};
	private _eventsBuffer: AnalyticsEvent[] = [];
	private _processedEvents: Record<string, ProcessedEventData[]> = {};
	private _processorsPool: EventBufferProcessor[] = [];

	private _observers: IAnalyticsObserver[] = [];

	constructor(params: { processorsAmount?: number }) {
		const { processorsAmount = Analytics.DEFAULT_PROCESSORS_AMOUNT } = params;

		for (let i = 0; i < processorsAmount; i += 1) {
			this._processorsPool.push(new EventBufferProcessor());
		}
	}

	subscribe = (observer: IAnalyticsObserver) => {
		this._observers.push(observer);
	};

	unsubscribe = (observer: IAnalyticsObserver) => {
		this._observers = this._observers.filter(o => o !== observer);
	};

	registerMedia = (data: Omit<IMedia, 'storyId' | 'cardId' | 'playbackId'>) => {
		const { player } = data;
		const playbackId = store.getState().user.storyPageViewId;
		const storyId = selectStoryId(store.getState())!;
		const cardId = selectCardId(store.getState())!;
		const media: IMedia = {
			...data,
			cardId,
			storyId,
			playbackId,
		};
		let watchtimeInterval: NodeJS.Timeout;

		if (this._medias[media.id] || !playbackId) {
			return;
		}

		this._sendEvent({
			type: 'impression',
			media,
		});

		const onInitialPlay = () => {
			this._medias[media.id].data = {
				...this._medias[media.id]?.data,
				engagedFired: false,
			};

			this._sendEvent({
				type: 'play',
				media,
			});
			onWatchTimeInterval();
		};

		const onPlay = () => {
			watchtimeInterval = setInterval(onWatchTimeInterval, Analytics.WATCHTIME_INTERVAL);
		};

		const onPause = () => {
			clearInterval(watchtimeInterval);
		};

		const onTimeUpdate = () => {
			const mediaData = this._medias[media.id]?.data ?? {};
			const currentTime = player.currentTime();

			if (currentTime === undefined) {
				return;
			}

			this._medias[media.id].data = mediaData;

			if (mediaData.currentTime !== undefined) {
				if (Math.abs(mediaData.currentTime - currentTime) > 1) {
					mediaData.wasSeeked = true;

					// seeked:
					this._sendEvent({
						type: 'watchtime',
						media,
						details: {
							watchtimeJumped: false,
							watchtimeLocation: mediaData.currentTime,
						},
					});
					this._sendEvent({
						type: 'watchtime',
						media,
						details: {
							watchtimeJumped: true,
							watchtimeLocation: currentTime,
						},
					});

					clearInterval(watchtimeInterval);
					watchtimeInterval = setInterval(onWatchTimeInterval, Analytics.WATCHTIME_INTERVAL);
				}

				if (!mediaData.wasSeeked && !mediaData.engagedFired && currentTime >= 3) {
					this._sendEvent({
						type: 'engaged_3s',
						media,
					});

					mediaData.engagedFired = true;
				}
			}

			mediaData.currentTime = currentTime;
		};

		const onWatchTimeInterval = () => {
			const currentTime = player.currentTime();

			if (currentTime === undefined) {
				return;
			}

			if (this._medias[media.id]?.data?.currentTime === currentTime) {
				return;
			}

			this._sendEvent({
				type: 'watchtime',
				media,
				details: {
					watchtimeJumped: false,
					watchtimeLocation: currentTime,
				},
			});
		};

		const onEnded = () => {
			this._sendEvent({
				type: 'ended',
				media,
			});
		};

		player.one('play', onInitialPlay);
		player.on('play', onPlay);
		player.on('pause', onPause);
		player.on('timeupdate', onTimeUpdate);
		player.on('ended', onEnded);

		this._medias[media.id] = {
			media,
			cleanup: () => {
				player.off('play', onInitialPlay);
				player.off('play', onPlay);
				player.off('pause', onPause);
				player.off('timeupdate', onTimeUpdate);
				player.off('ended', onEnded);
				clearInterval(watchtimeInterval);
			},
		};
	};

	unregisterMedia(id: string) {
		const mediaEntry = this._medias[id];

		if (mediaEntry) {
			mediaEntry.cleanup();
			delete this._medias[id];
		}
	}

	private _serializeEvent = (event: AnalyticsEvent) => {
		const detailsKey = event.details ? serializeObject(event.details) : '';
		const mediaKey = serializeObject({
			id: event.media.id,
			storyId: event.media.storyId,
			cardId: event.media.cardId,
			layerName: event.media.layerName,
			videoContentId: event.media.videoContentId,
			videoId: event.media.transcodingId,
			videoPlayerId: event.media.id,
			playbackId: event.media.playbackId,
			mediaKind: event.media.mediaKind ?? 'video',
		});

		return [event.type, mediaKey, detailsKey].filter(Boolean).join('_');

		function serializeObject(obj: Record<string, unknown>) {
			return Object.entries(obj)
				.sort(([key1], [key2]) => key1.localeCompare(key2))
				.map(([key, value]) => `${key}:${value}`)
				.join('_');
		}
	};

	private _sendEvent = (event: AnalyticsEvent) => {
		const eventKey = this._serializeEvent(event);

		if (this._processedEvents[eventKey]) {
			switch (event.type) {
				case 'impression':
				case 'play':
				case 'engaged_3s':
					return;

				case 'watchtime': {
					break;
				}

				default:
					return;
			}
		}

		this._eventsBuffer.push(event);
		this._processedEvents[eventKey] ??= [];
		this._processedEvents[eventKey].push({ timestamp: Date.now() });
		this._processEventsBuffer();
	};

	private _processEventsBuffer = () => {
		const processor = this._processorsPool.find(p => p.state === 'idle');
		if (!processor) return;

		const unprocessedEvent = this._eventsBuffer.shift();
		if (!unprocessedEvent) return;

		processor.run(() => {
			return this._notifyObservers(unprocessedEvent);
		}, this._processEventsBuffer);
	};

	private _notifyObservers = (event: AnalyticsEvent) => {
		return Promise.all(this._observers.map(observer => observer.track(event)));
	};
}

class EventBufferProcessor {
	private _state: 'idle' | 'processing' = 'idle';

	public get state() {
		return this._state;
	}

	public run = async (action: () => Promise<unknown>, onComplete: () => unknown) => {
		try {
			if (this._state === 'processing') {
				return;
			}

			this._state = 'processing';

			await action();
		} catch (e) {
			console.error('Analytics: Error processing event', e);
		} finally {
			this._state = 'idle';
			onComplete();
		}
	};
}

export class StorycardsAnalyticsObserver implements IAnalyticsObserver {
	async track(event: AnalyticsEvent) {
		if (!event.media.transcodingId) {
			return 0;
		}

		const commonProps = {
			playbackId: event.media.playbackId!,
			videoId: event.media.transcodingId,
			videoPlayerId: event.media.id,
		};

		switch (event.type) {
			case 'impression':
			case 'play':
			case 'engaged_3s':
				return store.dispatch(
					sendVideoEvent({
						event: event.type,
						properties: commonProps,
					})
				);

			case 'watchtime':
				return store.dispatch(
					sendVideoEvent({
						event: 'watchtime',
						properties: {
							...commonProps,
							watchtimeLocation: event.details.watchtimeLocation,
							watchtimeJumped: event.details.watchtimeJumped,
						},
					})
				);

			default:
				return 0;
		}
	}
}

export class GoogleAnalyticsObserver implements IAnalyticsObserver {
	private _mediaProgressMap: WeakMap<IMedia, number[]> = new WeakMap();

	async track(event: AnalyticsEvent) {
		const duration = event.media.player.duration() ?? 0;
		const currentTime = event.media.player.currentTime() ?? 0;
		const mediaUrl = event.media.player.currentSrc() ?? '';
		const mediaKind = event.media.mediaKind ?? 'video';
		const commonProps = {
			storyId: event.media.storyId,
			cardId: event.media.cardId,
			layerName: event.media.layerName,
			mediaContentId: event.media.videoContentId,
			mediaUrl,
			mediaDuration: duration,
			mediaKind,
		};

		switch (event.type) {
			case 'play':
				analytics.onVideoPlay({
					...commonProps,
				});
				break;

			case 'watchtime': {
				const TRACKING_PROGRESS_INTERVAL = 0.25;
				const progress = currentTime / duration;
				const roundedProgress = Math.floor(progress / TRACKING_PROGRESS_INTERVAL) * TRACKING_PROGRESS_INTERVAL;
				const trackedProgress = this._mediaProgressMap.get(event.media) ?? [];

				if (trackedProgress.includes(roundedProgress) || roundedProgress === 0 || roundedProgress === 1) {
					return;
				}

				analytics.onVideoProgress({
					...commonProps,
					mediaProgress: roundedProgress,
					mediaKind,
				});

				trackedProgress.push(roundedProgress);
				this._mediaProgressMap.set(event.media, trackedProgress);
				break;
			}

			case 'engaged_3s':
				analytics.onMediaEngaged3s({
					...commonProps,
					mediaKind,
				});
				break;

			case 'ended':
				analytics.onVideoProgress({
					...commonProps,
					mediaProgress: 1,
					mediaKind,
				});
				analytics.onVideoComplete({
					...commonProps,
					mediaKind,
				});
				break;

			default:
				break;
		}
	}
}

export default Analytics;
